<template>
    <DashedCard
        :title="title"
        :title-size="14"
        min-height="150px"
        :margin-bottom="45"
        :margin-top="40"
        :custom-style="{ padding: normalizeSize(32, true) }"
        custom-class-title="!ppv2-top-[-10px] !ppv2-px-[10px]"
    >
        <template #content>
            <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                <div
                    class="ppv2-min-w-[170px] item-wrapper ppv2-relative"
                    v-for="(item, i) in visibleItems"
                    :key="i"
                    :style="{
                        width: `calc((100% - 2 * ${normalizeSize(16)}) / 2)`,
                    }"
                >
                    <DashedCard
                        title-has-bg="!ppv2-bg-white !ppv2-text-[#0b1033] ppv2-py-[1px]"
                        :titleSize="10"
                        min-height="85px"
                        :margin-bottom="0"
                        :margin-top="0"
                        :custom-style="{ padding: '12px 17px' }"
                        :custom-class="[
                            'ppv2-opacity-80 hover:ppv2-opacity-100 transition-all duration-300',
                            item.selected ? 'ppv2-bg-black ppv2-bg-opacity-10' : '',
                        ]"
                        custom-class-title="!ppv2-px-[10px] !ppv2-top-[-8px] ppv2-rounded-[21px]"
                    >
                        <template #content>
                            <div class="ppv2-text-black">
                                <!-- Boutons d’action -->
                                <div class="actions">
                                    <SquarePen class="ppv2-cursor-pointer" :size="15" @click="$emit('edit', i, type)" />
                                    <Trash2 class="ppv2-cursor-pointer" :size="15" @click="$emit('delete', i, type)" />
                                </div>

                                <!-- Checkbox + contenu -->
                                <div class="d-flex align-items-center" :style="{ gap: normalizeSize(16, true) }">
                                    <div class="checkbox-col">
                                        <BaseCheckbox
                                            size="xs"
                                            v-model="item.selected"
                                            :id="'checkbox-' + type + '-' + i"
                                            @input="toggleSelection(i)"
                                            :has-border-checked="false"
                                        />
                                    </div>

                                    <div style="max-width: calc(100% - 36px); width: 100%">
                                        <div class="item-content">
                                            <div
                                                class="ppv2-text-black poppinsSemiBold ppv2-mt-1 ppv2-min-h-[33px] ppv2-max-w-[88%]"
                                                :style="{ fontSize: normalizeFontSize(13) }"
                                            >
                                                {{ item.name }}
                                            </div>
                                            <div class="details ppv2-text-black">
                                                <p><Users :size="13" /> {{ peopleLabel(item) }}</p>
                                                <p>
                                                    <RefreshCw :size="13" /> Mixage
                                                    {{
                                                        item.mix
                                                            ? $t('experience.step3.activated')
                                                            : $t('experience.step3.disabled')
                                                    }}
                                                </p>
                                                <p v-if="item.prestations_count !== undefined">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        :width="13"
                                                        :height="13"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        stroke-width="2"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    >
                                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                        <circle cx="9" cy="7" r="4" />
                                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                                    </svg>
                                                    {{
                                                        item.prestations_count <= 1
                                                            ? $t('experience.step3.usedInExperience', {
                                                                  count: item.prestations_count,
                                                              })
                                                            : $t('experience.step3.usedInExperiences', {
                                                                  count: item.prestations_count,
                                                              })
                                                    }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </DashedCard>
                </div>

                <!-- Global add button -->
                <div class="add-item" @click="$emit('add', type)">
                    <Plus class="ppv2-text-black ppv2-text-opacity-90" />
                </div>
            </div>

            <!-- Load More Button -->
            <div v-if="hasMoreItems" class="ppv2-flex ppv2-justify-center ppv2-mt-4">
                <BaseButton variant="outline" @click="loadMore">
                    {{ $t('common.loadMore') || 'Charger plus' }}
                </BaseButton>
            </div>
        </template>
    </DashedCard>
</template>

<script>
import { SquarePen, Trash2, Users, RefreshCw, Plus } from 'lucide-vue'
import BaseCheckbox from '../../../Shared/BaseCheckbox.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import TimeSpecific from './TimeSpecific.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'ConfigListInsalationEquipement',
    components: { DashedCard, BaseButton, TimeSpecific, SquarePen, Trash2, Users, RefreshCw, BaseCheckbox, Plus },
    props: {
        title: String,
        items: Array,
        type: String,
        activeField: String,
        singleSelection: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            limit: 100,
        }
    },
    computed: {
        hasSelection() {
            return this.items.some((i) => i.selected)
        },
        exampleLabel() {
            return this.type === 'installations'
                ? this.$t('experience.step3.yourFacilites')
                : this.type === 'equipements'
                  ? this.$t('experience.step3.yourEquipment')
                  : this.$t('experience.step3.example')
        },
        visibleItems() {
            return this.items.slice(0, this.limit)
        },
        hasMoreItems() {
            return this.items.length > this.limit
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        normalizeSizeVertical,
        peopleLabel(item) {
            if (this.type === 'installations') {
                return item.capacity + ' ' + this.$t('experience.step3.people')
            } else if (this.type === 'equipements') {
                return this.$t('experience.step3.stock') + `: ${item.capacity}`
            }
            return item.capacity
        },
        toggleSelection(i) {
            // Emit with correct state
            console.log('item=====', JSON.stringify(this.items[i]))
            this.$emit('toggle', { index: i, item: this.items[i], items: this.items, type: this.type })
            console.log(this.activeField, '------activeField')
        },
        loadMore() {
            this.limit += 12
        },
    },
}
</script>
<style scoped>
/* ✅ Layout fix */
.item-row {
    display: flex;
    align-items: center;
    /* Center checkbox vertically */
}

.checkbox-col {
    display: contents;
    justify-content: center;
    align-items: center;
    /* Center checkbox horizontally + vertically */
}

.item-content {
    display: flex;
    flex-direction: column;
}

.item-content .details p {
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 9px;
    margin: 4px 0;
}

.actions {
    position: absolute;
    top: 6px;
    right: 8px;
    display: flex;
    gap: 10px;
    margin-top: 5px;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.item-wrapper:hover .actions {
    opacity: 1;
}

.add-item {
    border: 0.67px solid #9c9af8;
    border-radius: 12px;
    padding: 30px 10px;
    text-align: center;
    color: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    transition: 0.3s ease;
    opacity: 0.75;
    width: 85px;
    height: 85px;
}

.add-item:hover {
    opacity: 1;
}

.plus {
    font-size: 32px;
    font-weight: bold;
    display: block;
    line-height: 1;
}
</style>
